/**
 * antd-table-saveas-excel / better-xlsx 导出的通用排版处理：空值、金额、自动列宽、冻结表头、序号列。
 * 纯函数，无 React 依赖，可独立单测。
 */
import moment from 'moment';
import { Excel } from 'antd-table-saveas-excel';

/** 页面上用来占位的空值，导出时留空单元格。 */
export const EXCEL_EMPTY_PLACEHOLDERS = ['-', '- / -', '-/-'];

export const EXCEL_AMOUNT_NUM_FMT = '#,##0.00';

export const EXCEL_ROW_NO_KEY = '__row_no__';

/** 第一列序号 No。数据行需要带 EXCEL_ROW_NO_KEY 字段，见 withExcelRowNo。 */
export const EXCEL_ROW_NO_COLUMN = {
  title: 'No',
  dataIndex: EXCEL_ROW_NO_KEY,
  key: EXCEL_ROW_NO_KEY,
  __numFmt__: '0' as const,
  __style__: { h: 'center' as const },
};

export const withExcelRowNo = <T extends object>(rows: T[]) =>
  rows.map((row, index) => ({ ...row, [EXCEL_ROW_NO_KEY]: index + 1 }));

// 约 24pt：每行统一高度，10 号字单行留足上下空白；多行内容由 fitExcelRowHeights 再加高
export const EXCEL_ROW_HEIGHT_CM = 0.85;

// 中灰格线：在白底上清楚可见，又不像纯黑那么重
const EXCEL_BORDER_COLOR = 'FF808080';

/** 统一排版的表头样式：深蓝底白字、居中、中灰格线。 */
export const EXCEL_STYLED_THEAD_CELL_STYLE = {
  background: 'FF31437c',
  color: 'FFFFFFFF',
  fontSize: 10,
  fontName: 'Helvetica',
  bold: true,
  border: true,
  borderColor: EXCEL_BORDER_COLOR,
  h: 'center' as const,
  v: 'center' as const,
  wrapText: true,
};

export const EXCEL_STYLED_TBODY_CELL_STYLE = {
  wrapText: true,
  fontName: 'Helvetica',
  fontSize: 10,
  border: true,
  borderColor: EXCEL_BORDER_COLOR,
  v: 'center' as const,
};

/**
 * 新建一个统一排版（表头 / 表体样式、行高）的 Excel，并建好 sheet。
 * 之后：需要的话画标题行 → addStyledExcelTable → finalizeExcelSheet → saveAs。
 */
export const createStyledExcel = (sheetName: string) => {
  const excel = new Excel();
  excel.defaultTheadCellStyle = EXCEL_STYLED_THEAD_CELL_STYLE as any;
  excel.defaultTbodyCellStyle = EXCEL_STYLED_TBODY_CELL_STYLE as any;
  return excel.setRowHeight(EXCEL_ROW_HEIGHT_CM, 'cm').addSheet(sheetName);
};

/** 表头占几行：有多币种子列（children）时两行。 */
export const getExcelHeaderDepth = (columns: any[]) =>
  columns.some(c => c.children?.length) ? 2 : 1;

/**
 * 加一张表：首列序号 No + columns + rows。返回数据前面一共有几行（titleRows + 表头行数），
 * 直接传给 finalizeExcelSheet 用来冻结表头。
 * 不用 str2num：它会把空的单号 / 备注变成 0、把纯数字单号转成数字；金额列在 toStyledExcelColumn 里已转成数字。
 */
export const addStyledExcelTable = (
  excel: Excel,
  columns: any[],
  rows: object[],
  {
    titleRows = 0,
    dataSourceOptions = {},
  }: { titleRows?: number; dataSourceOptions?: object } = {},
) => {
  excel
    .addColumns([EXCEL_ROW_NO_COLUMN, ...columns] as any)
    .addDataSource(withExcelRowNo(rows), { str2Percent: true, ...dataSourceOptions });
  return titleRows + getExcelHeaderDepth(columns);
};

/** 只包一段文字的 React 元素（如列表里的 <div>-</div> 占位）取出文字。 */
const unwrapTextElement = (value: any) =>
  value?.$$typeof && typeof value.props?.children === 'string' ? value.props.children : value;

/** 去掉末尾的换行和空白（如 "RACK\nDESK\n "），否则 Excel 会多显示一个空行，行高也会多算一行。 */
const trimTrailingBlank = (text: string) => text.replace(/\s+$/, '');

export const toExcelText = (value: any) => {
  let text = unwrapTextElement(value);
  if (typeof text === 'string') text = trimTrailingBlank(text);
  return text === null || text === undefined || EXCEL_EMPTY_PLACEHOLDERS.includes(text) ? '' : text;
};

/**
 * 当前界面语言，与 app.tsx 里运行时的 locale.getLocale 一致：URL ?locale= 优先，其次 umi_locale。
 * formatNumber（intl.formatNumber）就是按这个语言格式化金额的。
 */
const getCurrentLocale = (): string => {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('locale');
    return fromQuery || window.localStorage.getItem('umi_locale') || 'en-US';
  } catch {
    return 'en-US';
  }
};

/** 某个语言的千分位和小数点符号，如 es-ES 为 { group: '.', decimal: ',' }。 */
export const getNumberSeparators = (locale: string = getCurrentLocale()) => {
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    return {
      group: parts.find(p => p.type === 'group')?.value ?? ',',
      decimal: parts.find(p => p.type === 'decimal')?.value ?? '.',
    };
  } catch {
    return { group: ',', decimal: '.' };
  }
};

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * 金额写成数字单元格。excelRender 可能返回 formatNumber 出来的字符串，它按当前语言格式化，
 * 所以要按该语言的千分位 / 小数点解析（es-ES 的 "1.234,56" 是 1234.56，不是 1.23456）。
 */
export const toExcelNumber = (value: any, locale?: string) => {
  if (value === null || value === undefined || value === '' || typeof value === 'number') {
    return value ?? '';
  }
  if (typeof value === 'object') return value;
  const text = String(value).trim();
  const { group, decimal } = getNumberSeparators(locale);
  // toFixed 之类的机器格式（1234.50）直接用。只有"点号当千分位"语言里的 1.234 / 1.234.567
  // 这种纯千分位写法才按当地格式解析（千分位一定是 3 位一组，小数点不会出现多次）。
  const isLocaleGrouped = group === '.' && /^-?\d{1,3}(\.\d{3})+$/.test(text);
  if (!isLocaleGrouped && /^-?\d+(\.\d+)?$/.test(text)) return Number(text);
  const normalized = text
    // 千分位符号，以及 fr-FR 等语言用作千分位的各种空格
    .replace(new RegExp(`[${escapeRegExp(group)}\\s\u00a0\u202f]`, 'g'), '')
    .replace(decimal, '.')
    .replace('\u2212', '-');
  const num = /^-?\d+(\.\d+)?$/.test(normalized) ? Number(normalized) : NaN;
  return Number.isFinite(num) ? num : toExcelText(value);
};

/** 文本在 Excel 里大致占的字符宽度：中日韩全角字符按 2 算，多行取最长一行。 */
export const measureExcelText = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0;
  const text = typeof value === 'number' ? value.toLocaleString('en-US') : String(value);
  return Math.max(
    ...text.split(/\r?\n/).map(line => {
      let width = 0;
      for (const ch of line) {
        width += /[\u2E80-\u9FFF\uAC00-\uD7AF\uF900-\uFAFF\uFF00-\uFFEF]/.test(ch) ? 2 : 1;
      }
      return width;
    }),
  );
};

const isDateNumFmt = (numFmt?: string) => !!numFmt && /(yy|dd|mmm)/i.test(numFmt);

/**
 * 序号列 No：addStyledExcelTable 总是把它放在第 0 列。只看第 0 列，免得业务列标题恰好叫 "No" 时也被压窄；
 * 同时要求表头是 "No"，没有序号列的表第 0 列照常按 min 计算。
 */
const isExcelRowNoColumn = (sheet: any, c: number, headerRows: number) =>
  c === 0 &&
  sheet.rows
    .slice(0, headerRows)
    .some(
      (row: any) => row.cells[0]?.value === EXCEL_ROW_NO_COLUMN.title && !(row.cells[0].hMerge > 0),
    );

/** 列上通过 __style__.h 显式指定了对齐（库默认是 general），自动居中不能覆盖它。 */
const hasExplicitAlign = (cell: any) =>
  !['', 'general', 'center', undefined].includes(cell.style?.align?.h);

/**
 * 能否居中的"短值"：日期；不带千分位 / 小数 / 百分号格式、显示宽度 ≤ maxWidth 的数字；
 * 显示宽度 ≤ maxWidth 的单行文字。金额、百分比保持靠右，数位才对得齐。
 */
const isShortExcelCell = (cell: any, width: number, maxWidth: number) => {
  if (hasExplicitAlign(cell)) return false;
  if (typeof cell.value === 'number') {
    if (isDateNumFmt(cell.numFmt)) return true;
    if (/[#%]|0\.0/.test(cell.numFmt || '')) return false;
    return width <= maxWidth;
  }
  return width <= maxWidth && !String(cell.value).includes('\n');
};

const hasExcelValue = (cell: any) => cell.value !== '' && cell.value != null;

/**
 * 按内容自动设置列宽，并把短值列居中，最后按换行加高行。一次遍历所有单元格，大表也不会多算。
 * - 列不能太窄（min），否则内容挤得换行、行被撑高；序号列可以窄到 rowNoMin
 * - 表头会换行，所以表头宽度最多只算 headerMax；合并的表头单元格（多币种父列）不参与计算
 * - centerMaxWidth > 0 时：一列里所有数据（不含表头和合计行 skipRow）都是短值，整列居中。
 *   日期、Y/N、FCL 这类居中比默认的"文字靠左、数字靠右"整齐；列上显式写了 __style__.h 的不动
 */
export const autoFitExcelColumns = (
  sheet: any,
  {
    headerRows = 1,
    min = 12,
    rowNoMin = 6,
    max = 50,
    headerMax = 26,
    padding = 2,
    centerMaxWidth = 0,
    skipRow = -1,
  }: {
    headerRows?: number;
    min?: number;
    rowNoMin?: number;
    max?: number;
    headerMax?: number;
    padding?: number;
    centerMaxWidth?: number;
    skipRow?: number;
  } = {},
) => {
  const widths: number[] = [];
  // 每列的数据是否全是短值；undefined 表示这一列还没有非空数据
  const short: (boolean | undefined)[] = [];
  sheet.rows.forEach((row: any, r: number) => {
    row.cells.forEach((cell: any, c: number) => {
      if (r < headerRows && cell.hMerge > 0) return;
      let width = measureExcelText(cell.value);
      if (typeof cell.value === 'number' && isDateNumFmt(cell.numFmt)) {
        // 日期序列号按显示格式的长度算（mm/dd/yy hh:mm 显示 14 个字符，数字本身只有 5 位）
        width = String(cell.numFmt).replace(/AM\/PM/, 'AM').length;
      } else if (typeof cell.value === 'number' && /0\.00/.test(cell.numFmt || '')) {
        width += 3;
      }
      if (
        centerMaxWidth > 0 &&
        r >= headerRows &&
        r !== skipRow &&
        !(cell.hMerge > 0) &&
        hasExcelValue(cell) &&
        short[c] !== false
      ) {
        short[c] = isShortExcelCell(cell, width, centerMaxWidth);
      }
      if (r < headerRows) width = Math.min(width, headerMax);
      widths[c] = Math.max(widths[c] || 0, width);
    });
  });
  sheet.cols.forEach((col: any, c: number) => {
    const colMin = isExcelRowNoColumn(sheet, c, headerRows) ? rowNoMin : min;
    col.width = Math.min(max, Math.max(colMin, (widths[c] || 0) + padding));
  });
  if (short.some(Boolean)) {
    sheet.rows.forEach((row: any, r: number) => {
      if (r < headerRows || r === skipRow) return;
      row.cells.forEach((cell: any, c: number) => {
        if (short[c] && !(cell.hMerge > 0) && hasExcelValue(cell)) cell.style.align.h = 'center';
      });
    });
  }
  fitExcelRowHeights(sheet);
};

/** 10 号字一行大约占的高度（pt），以及单元格上下留白。 */
const EXCEL_LINE_HEIGHT_PT = 13;
const EXCEL_ROW_PADDING_PT = 4;

/** 文本在给定列宽（字符数）下换行后的行数。 */
export const countExcelLines = (value: any, colWidth: number): number => {
  if (value === null || value === undefined || value === '' || typeof value === 'number') return 1;
  const usable = Math.max(1, colWidth - 1);
  return trimTrailingBlank(String(value))
    .split(/\r?\n/)
    .reduce((sum, line) => sum + Math.max(1, Math.ceil(measureExcelText(line) / usable)), 0);
};

/**
 * 按换行后的行数加高行（只加不减）。setRowHeight 设的是固定行高，Excel 打开时不会自动撑开，
 * 换行的表头（列宽被 headerMax 限制）和多行单元格会被截掉。合并单元格（标题行）不参与计算。
 * 列宽确定之后调用（autoFitExcelColumns 已经调用）。
 */
export const fitExcelRowHeights = (sheet: any) => {
  sheet.rows.forEach((row: any) => {
    let lines = 1;
    row.cells.forEach((cell: any, c: number) => {
      if (cell.hMerge > 0) return;
      const colWidth = sheet.cols[c]?.width || 10;
      lines = Math.max(lines, countExcelLines(cell.value, colWidth));
    });
    if (lines <= 1) return;
    const needed = lines * EXCEL_LINE_HEIGHT_PT + EXCEL_ROW_PADDING_PT;
    if (!row.height || row.height < needed) row.height = needed;
  });
};

/**
 * 冻结表头，并修正 better-xlsx 生成的、会让 Excel 提示"需要修复"的两处 XML：
 * - <pane> 被追加在 <selection> 之后，OOXML 要求 pane 在前
 * - 空字符串单元格写成 <c t="s"/>（共享字符串类型却没有值）
 * 必须在 addSheet 之后、saveAs 之前调用。
 */
export const freezeExcelHeader = (sheet: any, headerRows = 1) => {
  const topLeftCell = `A${headerRows + 1}`;
  sheet.sheetViews = [
    {
      pane: {
        xSplit: 0,
        ySplit: headerRows,
        topLeftCell,
        activePane: 'bottomLeft',
        state: 'frozen',
      },
    },
  ];
  sheet.afterMake = (xSheet: any) => {
    const view = xSheet.sheetViews.children[0];
    const panes = view.children.filter((n: any) => n.ySplit !== undefined);
    const selections = view.children.filter((n: any) => n.sqref !== undefined);
    selections.forEach((n: any) => {
      n.pane = 'bottomLeft';
      n.activeCell = topLeftCell;
      n.sqref = topLeftCell;
    });
    view.children = [...panes, ...selections];
    xSheet.sheetData.children.forEach((row: any) =>
      row.children.forEach((c: any) => {
        if (c.t === 's' && (c.v === null || c.v === undefined)) c.t = undefined;
      }),
    );
  };
};

/** 一列里所有内容都不超过这么多字符时整列居中。 */
const EXCEL_CENTER_MAX_WIDTH = 10;

/**
 * 导出前的收尾：冻结表头 + 自动列宽 + 短值列居中 + 行高。在 addDataSource 之后、saveAs 之前调用。
 * 末尾有合计行（styleExcelTotalRow）时传 hasTotalRow，合计行不参与居中判断。
 */
export const finalizeExcelSheet = (
  sheet: any,
  headerRows = 1,
  { hasTotalRow = false }: { hasTotalRow?: boolean } = {},
) => {
  freezeExcelHeader(sheet, headerRows);
  autoFitExcelColumns(sheet, {
    headerRows,
    centerMaxWidth: EXCEL_CENTER_MAX_WIDTH,
    skipRow: hasTotalRow ? findLastContentRowIndex(sheet) : -1,
  });
};

const isAmountNumFmt = (numFmt?: string) => !!numFmt && /^(#,##)?0\.0+$/.test(numFmt);

/**
 * 统一单元格内容：金额列（__numFmt__ 为 0.00 / #,##0.00）转数字、千分位、右对齐；
 * 其它列把 '-' 之类的占位符导出为空。列宽交给 autoFitExcelColumns。
 */
export const toStyledExcelColumn = <T extends Record<string, any>>(column: T): T => {
  const isAmount = isAmountNumFmt(column.__numFmt__);
  // 和库一样：没有 excelRender 时退回 render，否则只有 render 的列会导出成原始值或空白
  const render = column.excelRender ?? column.render;
  return {
    ...column,
    ...(isAmount && {
      __numFmt__: EXCEL_AMOUNT_NUM_FMT,
      __style__: { ...column.__style__, h: 'right' },
    }),
    excelRender: (value: any, record: any, index: number) => {
      const rendered = render ? render(value, record, index) : value;
      return isAmount ? toExcelNumber(rendered) : toExcelText(rendered);
    },
  };
};

/**
 * 列上声明导出类型，不再在各个页面里按 key 白名单判断：
 * - 'date'：值是 YYYY-MM-DD 开头的字符串，导出为日期单元格
 * - 'amount'：导出为数字 + 千分位
 */
export type ExcelColumnType = 'date' | 'amount';
export interface ExcelColumnMeta {
  __excelType__?: ExcelColumnType;
}

/** 按列的 __excelType__ 转成日期 / 金额 / 文本列。dateNumFmt 见 getExcelDateNumberFormat。 */
export const toTypedExcelColumn = <T extends Record<string, any>>(
  column: T,
  dateNumFmt: string,
): T => {
  if (column.__excelType__ === 'date') return toExcelDateColumn(column, dateNumFmt);
  if (column.__excelType__ === 'amount') {
    return toStyledExcelColumn({ ...column, __numFmt__: '0.00' });
  }
  return toStyledExcelColumn(column);
};

// Return an Excel date serial number (days since 1899-12-30, time as the fraction) for
// date cells; the column's __numFmt__ (getExcelDateNumberFormat) controls how it shows.
// A real date cell sorts and filters as a date in Excel -- the formatted string this used
// to return showed the "text date with 2-digit year" warning on every cell.
//
// The date part is read with moment.utc from its YYYY-MM-DD prefix so no local-timezone
// day shift can creep in (the original bug from b25c032). We do not use the library's
// TypeDate path: it crashes on empty values (new Date('') -> NaN -> unopenable xlsx).
// Empty/invalid input returns ''. The third argument (date format) is accepted and ignored
// so existing call sites keep working.
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const DAY_MS = 24 * 60 * 60 * 1000;

export function getExcelDateCellValue(date: any, hour?: any, _dateFormat?: string): number | '' {
  if (!date) return '';

  let day = moment.utc(String(date).slice(0, 10), 'YYYY-MM-DD', true);
  if (!day.isValid()) {
    // 其它格式（Date 对象、05/01/2024 等）按本地时间解析，取页面上显示的那个本地日期；
    // 直接 moment.utc(date) 会先按本地时区解析再取 UTC 日期，东八区会早一天。
    const local = moment(date);
    if (!local.isValid()) return '';
    day = moment.utc(local.format('YYYY-MM-DD'), 'YYYY-MM-DD');
  }

  const serial = (day.valueOf() - EXCEL_EPOCH_MS) / DAY_MS;
  const minutes = parseExcelTimeMinutes(hour);
  return minutes === null ? serial : serial + minutes / (24 * 60);
}

/**
 * 解析时间：14:30、1430、0600、14（只有小时）、2:30 PM、14:30:00。
 * 小时 / 分钟越界或无法识别（TBA）时返回 null，只导出日期。
 */
export const parseExcelTimeMinutes = (hour: any): number | null => {
  const match = String(hour ?? '')
    .trim()
    .match(/^(\d{1,2})(?::?(\d{2}))?(?::\d{2})?\s*(?:([AaPp])\.?\s*[Mm]\.?)?(?!\d)/);
  if (!match) return null;
  let h = Number(match[1]);
  const m = Number(match[2] || 0);
  const meridiem = match[3]?.toLowerCase();
  if (meridiem) {
    if (h < 1 || h > 12) return null;
    if (meridiem === 'p' && h !== 12) h += 12;
    if (meridiem === 'a' && h === 12) h = 0;
  }
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
};

/**
 * 日期列：excelRender 返回的 YYYY-MM-DD 开头的字符串写成真正的日期单元格，
 * 显示格式由 numFmt（getExcelDateNumberFormat）决定；其它值按 toExcelText 处理。
 */
export const toExcelDateColumn = <T extends Record<string, any>>(column: T, numFmt: string): T => {
  const render = column.excelRender ?? column.render;
  return {
    ...column,
    __numFmt__: numFmt,
    excelRender: (value: any, record: any, index: number) => {
      const rendered = render ? render(value, record, index) : value;
      return typeof rendered === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rendered)
        ? getExcelDateCellValue(rendered)
        : toExcelText(rendered);
    },
  };
};

/** 最后一个有内容的行。两行表头（多币种）时库会在末尾多建一个空行，所以不能直接取最后一行。 */
const findLastContentRowIndex = (sheet: any): number => {
  for (let r = sheet.rows.length - 1; r >= 0; r -= 1) {
    if (sheet.rows[r].cells.some(hasExcelValue)) return r;
  }
  return -1;
};

/** 把最后一个有内容的行标成合计行：加粗 + 浅灰底。在 addDataSource 之后调用。 */
export const styleExcelTotalRow = (sheet: any) => {
  const row = sheet.rows[findLastContentRowIndex(sheet)];
  row?.cells.forEach((cell: any) => {
    cell.style.font.bold = true;
    cell.style.fill.patternType = 'solid';
    cell.style.fill.fgColor = 'FFF2F2F2';
    cell.style.fill.bgColor = 'FFFFFFFF';
  });
};

/**
 * 下拉类型的列（ProTable valueEnum）在没写 excelRender 时，导出页面上显示的文字而不是原始值，
 * 比如 wire -> Wire、receivable -> 应收。
 */
export const valueEnumExcelRender = (valueEnum: Record<string, any>) => (value: any) => {
  const option = valueEnum[value];
  return (option !== null && typeof option === 'object' ? option.text : option) ?? value;
};
